import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import Swal from "sweetalert2";
import moment from "moment";
import { NumericFormat } from "react-number-format";

import Footer from "../../Component/Footer";
import Header from "../../Component/Header";
import Menu from "../../Component/Menu";
import { currentTenant, thousandsdisplay, userid } from "../../Common/Credentials";
import { spin } from "../../Common/NewLoader";
import { salesid } from "../../Common/Permissions";
import PosService from "./PosService";

import ViewIcon from "../../images/Icons/preview.png";

// Import CSS
import '../../NewUI/NewUI.css';
import Breadcrumb from "../../NewUI/NewComponents/Breadcrumb";

const POSTable = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const navigate = useNavigate();

  // Search highlighting methods
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part)
        ? React.createElement('mark', {
          key: index,
          className: 'UI-search-highlight'
        }, part)
        : part
    );
  };

  // Handle resize
  const handleResize = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setShowSearch(false);
    }
  };

  const refreshData = useCallback(() => {
    setLoading(true);

    PosService.GetPosoftenant(currentTenant)
      .then((response) => {
        const sortedData = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setData(sortedData);
        setFilteredData(sortedData);
        setLoading(false);
      })
      .catch((e) => {
        if (e.response?.data?.error === "Unauthorized") {
          Swal.fire({
            title: "Your Session is Lost: Please login Back to resume",
            text: ".",
            icon: "error",
            confirmButtonText: "Ok",
            confirmButtonColor: "#152f5f",
          }).then(() => navigate("/"));
        }
        setLoading(false);
      });
  }, [currentTenant, navigate]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setShowSearch(true);
    }
    window.addEventListener('resize', handleResize);

    if (!userid) {
      window.confirm("Your Session is Lost: Please login Back to resume");
      navigate("/");
      return;
    }

    refreshData();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [navigate, refreshData]);

  useEffect(() => {
    updateFilteredData();
  }, [data, searchTerm]);

  // Sorting function
  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key] || '';
      let bValue = b[sortConfig.key] || '';

      // Handle date sorting
      if (sortConfig.key === 'createdAt') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      // Handle numeric sorting for total amount
      else if (sortConfig.key === 'totalinctax') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const updateFilteredData = () => {
    let filtered;
    if (!searchTerm.trim()) {
      filtered = data;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = data.filter(item =>
        item.customer?.toLowerCase().includes(lowerSearchTerm) ||
        item.contactno?.toLowerCase().includes(lowerSearchTerm) ||
        item.paymentmode?.toLowerCase().includes(lowerSearchTerm) ||
        moment(item.createdAt).format('DD-MM-YYYY HH:mm').toLowerCase().includes(lowerSearchTerm) ||
        item.totalinctax?.toString().includes(lowerSearchTerm)
      );
    }
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  // Get current page data for pagination
  const getCurrentPageData = () => {
    const sortedData = getSortedData(filteredData);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedData.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  // Format currency display
  const formatCurrency = (value) => {
    return (
      <NumericFormat
        displayType={"text"}
        value={value}
        decimalScale={2}
        fixedDecimalScale={true}
        thousandSeparator={true}
        thousandsGroupStyle={thousandsdisplay}
      />
    );
  };

  // Render pagination controls
  const renderPagination = () => {
    if (filteredData.length <= itemsPerPage) return null;

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    return (
      <div className={`UI-pagination-container ${isMobile ? 'UI-pagination-container-mobile' : ''}`}>
        <div className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} sales
        </div>
        <div className={`UI-pagination-controls ${isMobile ? 'UI-pagination-controls-mobile' : ''}`}>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className={`UI-page-size-select ${isMobile ? 'UI-page-size-select-mobile' : ''}`}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'first' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('first')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'First' : 'First'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'prev' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('prev')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Prev' : 'Previous'}
          </button>

          <span className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'next' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('next')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Next' : 'Next'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'last' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('last')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Last' : 'Last'}
          </button>
        </div>
      </div>
    );
  };

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fa fa-sort UI-sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fa fa-sort-up UI-sort-icon UI-sort-active" />
      : <i className="fa fa-sort-down UI-sort-icon UI-sort-active" />;
  };

  const renderTable = () => {
    const currentPageData = getCurrentPageData();

    if (filteredData.length === 0) {
      return (
        <div className={`UI-no-data-message ${isMobile ? 'UI-no-data-message-mobile' : ''}`}>
          {searchTerm ? 'No sales found matching your search' : 'No sales available'}
        </div>
      );
    }

    return (
      <>
        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`}>
            <thead className="UI-table-header">
              <tr>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'customer' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('customer')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Customer
                    <SortIndicator columnKey="customer" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'contactno' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('contactno')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Contact No.
                    <SortIndicator columnKey="contactno" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'createdAt' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('createdAt')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Date
                    <SortIndicator columnKey="createdAt" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'totalinctax' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('totalinctax')}
                  style={{ cursor: 'pointer', textAlign: 'right' }}
                >
                  <div className="UI-th-content">
                    Total Amount
                    <SortIndicator columnKey="totalinctax" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'paymentmode' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('paymentmode')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Payment Mode
                    <SortIndicator columnKey="paymentmode" />
                  </div>
                </th>
                <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`}>View Details</th>
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((item) => (
                <tr
                  key={item.id}
                  className={`UI-table-row ${hoveredRow === item.id ? 'UI-table-row-hovered' : ''} ${isMobile ? 'UI-table-row-mobile' : ''}`}
                  onMouseEnter={() => setHoveredRow(item.id)}
                  onMouseLeave={() => {
                    setHoveredRow(null);
                    setHoveredAction(null);
                  }}
                >
                  {/* Customer Cell */}
                  <td
                    className={`UI-td UI-service-name ${hoveredRow === item.id ? 'UI-service-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(item.customer, searchTerm)}
                  </td>

                  {/* Contact No. Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(item.contactno, searchTerm)}
                  </td>

                  {/* Date Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {moment(item.createdAt).format('DD-MM-YYYY HH:mm')}
                  </td>

                  {/* Total Amount Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                    style={{ textAlign: 'right' }}
                  >
                    {formatCurrency(item.totalinctax)}
                  </td>

                  {/* Payment Mode Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(item.paymentmode, searchTerm)}
                  </td>

                  {/* View Details Cell */}
                  <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                    <div className="UI-action-buttons">
                      <Tooltip title="View Sale Details" arrow slotProps={{
                        popper: {
                          sx: { pointerEvents: 'none' },
                        },
                      }}>
                        <Link
                          to={{
                            pathname: "/posdetails",
                            state: item
                          }}
                          className={`UI-action-button ${hoveredAction === `view-${item.id}` ? 'UI-view-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                          onMouseEnter={() => setHoveredAction(`view-${item.id}`)}
                          onMouseLeave={() => setHoveredAction(null)}
                        >
                          <img
                            src={ViewIcon}
                            alt="View Details"
                            className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `view-${item.id}` ? 'UI-action-icon-hover' : ''}`}
                          />
                          {isMobile && <span className="UI-action-label">View</span>}
                        </Link>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </>
    );
  };

  return (
    <div>
      <Header />
      <Menu />
      <div className="content-wrapper">
        <div className={`UI-container ${isMobile ? 'UI-container-mobile' : ''}`}>
          <div className="UI-card">
            {/* Header */}
            <div className={`UI-header ${isMobile ? 'UI-header-mobile' : ''}`}>
            <Breadcrumb breadcrumbs={[{name:"Home",path:"/sales"},{name:"Point of Sale",path:null}]}/>

              <div className={`UI-header-content ${isMobile ? 'UI-header-content-mobile' : ''}`}>
                <div>
                  <h1 className={`UI-title ${isMobile ? 'UI-title-mobile' : ''}`}>
                    Point of Sale
                  </h1>
                  <p className={`UI-subtitle ${isMobile ? 'UI-subtitle-mobile' : ''}`}>
                    Manage point of sale transactions
                  </p>
                </div>

                <div className={`UI-header-actions ${isMobile ? 'UI-header-actions-mobile' : ''}`}>
                  {/* Mobile Search */}
                  {isMobile && (
                    <input
                      type="text"
                      placeholder="Search sales..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="UI-mobile-search-input"
                    />
                  )}

                  <div className={`UI-buttons-row ${isMobile ? 'UI-buttons-row-mobile' : ''}`}>
                    {/* Desktop Search Toggle */}
                    {!isMobile && (
                      <div
                        className={`UI-search-container ${showSearch ? 'UI-search-container-expanded' : ''} ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                        onClick={() => {
                          if (!showSearch) setShowSearch(true);
                        }}
                        onMouseEnter={() => setHovered("search")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i
                          className="fa fa-search UI-search-icon"
                        />
                        {!showSearch && "Search"}
                        {showSearch && (
                          <input
                            type="text"
                            placeholder="Search sales..."
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="UI-search-input"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            onBlur={() => {
                              if (searchTerm.trim() === "") {
                                setShowSearch(false);
                              }
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* New Point of Sale button */}
                    {(salesid === '2' || salesid === '3') && (
                      <Link
                        to="/newpointofsale"
                        className={`UI-btn UI-btn-primary ${hovered === "new" ? 'UI-btn-primary-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                        onMouseEnter={() => setHovered("new")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i className="fa fa-plus" /> {isMobile ? "New" : "New Sale"}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="UI-loading-container">
                {spin}
              </div>
            ) : (
              renderTable()
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default POSTable;